
import { useMemo } from 'react';
import { Poll } from '@/types/poll';

interface FilteredPollsProps {
  polls: Poll[];
  status: string;
  countryCode: string | null;
  constituencyCode: string | null;
  category: string | null;
  year: string | null;
  search: string;
}

export const useFilteredPolls = ({
  polls,
  status,
  countryCode,
  constituencyCode,
  category,
  year,
  search
}: FilteredPollsProps) => {
  
  const filteredPolls = useMemo(() => {
    return polls.filter(poll => {
      // Filter by status
      if (status !== 'all') {
        if (status === 'active' && poll.status !== 'live') return false;
        if (status === 'closed' && poll.status !== 'closed') return false;
      }
      
      // Filter by country
      if (countryCode && poll.country_code !== countryCode) {
        // Allow global polls (no country_code) to show up for all countries
        if (poll.country_code !== null) return false;
      }
      
      // Filter by constituency
      if (constituencyCode && poll.constituency_code !== constituencyCode) {
        return false;
      }
      
      // Filter by category
      if (category && poll.category_id !== category) {
        return false;
      }
      
      // Filter by year
      if (year && poll.created_at) {
        const pollYear = new Date(poll.created_at).getFullYear().toString();
        if (pollYear !== year) return false;
      }
      
      // Filter by search text
      if (search) {
        const searchLower = search.toLowerCase();
        const titleMatch = poll.title.toLowerCase().includes(searchLower);
        const descriptionMatch = poll.description?.toLowerCase().includes(searchLower);
        const pollNumberMatch = poll.poll_number?.toLowerCase().includes(searchLower);
        
        if (!titleMatch && !descriptionMatch && !pollNumberMatch) return false;
      }
      
      return true;
    });
  }, [polls, status, countryCode, constituencyCode, category, year, search]);
  
  // Calculate statistics about the filtered results
  const activeFiltersCount = 
    (status !== 'all' ? 1 : 0) +
    (countryCode ? 1 : 0) +
    (constituencyCode ? 1 : 0) +
    (category ? 1 : 0) +
    (year ? 1 : 0) +
    (search ? 1 : 0);
  
  return { 
    filteredPolls,
    activeFiltersCount
  };
};

export default useFilteredPolls;
